import { useEffect, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { UserProfile, UserSecurity, UserSessions, WorkOsWidgets } from '@workos-inc/widgets'
import { Heading, Text, Box, Flex, Card } from '@radix-ui/themes'
import { useMe } from '../../hooks/useMe'
import { useWidgetToken } from '../../hooks/useWidgetToken'
import { lightenColor, darkenColor } from '../../../utils/color'
import { PhoneProfileRow } from '../../../components/profile/PhoneProfileRow'
import { NotificationPreferences } from '../../../components/profile/NotificationPreferences'

/**
 * Generate Radix accent CSS variable overrides from a single hex color.
 * Radix Themes uses --accent-1 (lightest) through --accent-12 (darkest).
 * The primary button uses --accent-9, hover uses --accent-10.
 */
function buildAccentOverrides(hex: string): React.CSSProperties {
  return {
    '--accent-1': lightenColor(hex, 95),
    '--accent-2': lightenColor(hex, 90),
    '--accent-3': lightenColor(hex, 80),
    '--accent-4': lightenColor(hex, 70),
    '--accent-5': lightenColor(hex, 60),
    '--accent-6': lightenColor(hex, 45),
    '--accent-7': lightenColor(hex, 30),
    '--accent-8': lightenColor(hex, 15),
    '--accent-9': hex,
    '--accent-10': darkenColor(hex, 8),
    '--accent-11': darkenColor(hex, 20),
    '--accent-12': darkenColor(hex, 35),
    '--accent-a9': hex,
    '--accent-a10': darkenColor(hex, 8),
    '--accent-contrast': '#fff',
    '--woswidgets-accent-color': hex,
  } as React.CSSProperties
}

export function OnboardingProfile() {
  const getProfileToken = useWidgetToken([])
  const getSecurityToken = useWidgetToken([])
  const getSessionsToken = useWidgetToken([])
  const queryClient = useQueryClient()

  const { data: meData } = useMe()

  // SSO users' passwords/MFA are owned by their identity provider, so the
  // WorkOS Password & MFA card is hidden for them. Profile and Active Sessions
  // stay visible.
  const isSsoManaged = meData?.is_sso_managed ?? false

  const brandColor = meData?.tenant?.primary_color || '#3b82f6'
  const accentOverrides = useMemo(() => buildAccentOverrides(brandColor), [brandColor])

  // Invalidate auth-me cache when leaving the profile page
  // so any changes made via widgets are reflected elsewhere
  useEffect(() => {
    return () => {
      queryClient.invalidateQueries({ queryKey: ['auth-me'] })
    }
  }, [queryClient])

  return (
    <Box>
      <Heading size={{ initial: '5', sm: '6' }} mb="2">Profile</Heading>
      <Text size={{ initial: '2', sm: '3' }} color="gray" mb={{ initial: '4', sm: '6' }} as="p">
        Manage your account details and security settings.
      </Text>

      <Flex direction="column" gap={{ initial: '4', sm: '6' }} maxWidth="640px">
        {/* WorkOS Profile Widget - avatar, display name, connected accounts */}
        <Card size={{ initial: '2', sm: '3' }}>
          <Heading size={{ initial: '3', sm: '4' }} mb="1">Profile</Heading>
          <Text size="2" color="gray" mb={{ initial: '3', sm: '4' }} as="p">
            Manage your avatar, display name, and connected accounts.
          </Text>

          <Box style={accentOverrides}>
            <WorkOsWidgets>
              <UserProfile authToken={getProfileToken} />
            </WorkOsWidgets>
            <PhoneProfileRow />
          </Box>
        </Card>

        {/* Notifications - personal email preferences. Renders nothing for
            users with no reports (the only email here is the manager digest). */}
        <NotificationPreferences />

        {/* WorkOS Security Widget - password, MFA. Hidden for SSO users:
            their password/MFA is owned by their identity provider. */}
        {!isSsoManaged && (
          <Card size={{ initial: '2', sm: '3' }}>
            <Heading size={{ initial: '3', sm: '4' }} mb="1">Security</Heading>
            <Text size="2" color="gray" mb={{ initial: '3', sm: '4' }} as="p">
              Manage your password and multi-factor authentication settings.
            </Text>

            <Box style={accentOverrides}>
              <WorkOsWidgets>
                <UserSecurity authToken={getSecurityToken} />
              </WorkOsWidgets>
            </Box>
          </Card>
        )}

        {/* WorkOS Sessions Widget - view/revoke sessions */}
        <Card size={{ initial: '2', sm: '3' }}>
          <Heading size={{ initial: '3', sm: '4' }} mb="1">Active sessions</Heading>
          <Text size="2" color="gray" mb={{ initial: '3', sm: '4' }} as="p">
            View and manage your active sessions across devices.
          </Text>

          <Box style={accentOverrides}>
            <WorkOsWidgets>
              <UserSessions authToken={getSessionsToken} />
            </WorkOsWidgets>
          </Box>
        </Card>
      </Flex>
    </Box>
  )
}
